import React from "react";
import { Provider } from "react-redux";
import { persistStore } from "redux-persist";
import { PersistGate } from "redux-persist/integration/react";
import { getConfiguredStore } from "../store";
import { fetchConfigRaw } from "../store/config";
import { UIConfig } from "../types/UIConfig";
import { App } from "./App";
import { Routes, Route, useParams } from "react-router-dom";
import {
  Organization,
  getOrg,
  lookupOrgByHostname,
} from "../types/Organization";
import { GenericError } from "../components/Feedback/GenericError";
import { pathRoutingEnabled } from "../services/urlBuilder";
import { Loading } from "../components/Feedback/Loading";
import { ChakraProvider, Flex } from "@chakra-ui/react";

const Root: React.FC = () => {
  const [uiConfig, setUiConfig] = React.useState<UIConfig | undefined>(
    undefined
  );
  const [organization, setOrganization] = React.useState<
    Organization | undefined
  >();
  const [fetchError, setFetchError] = React.useState<Error | undefined>(
    undefined
  );

  const { orgPath } = useParams();

  React.useEffect(() => {
    const fetch = async () => {
      try {
        let org: Organization | undefined = undefined;
        if (pathRoutingEnabled()) {
          org = await getOrg(orgPath);
        } else {
          org = await lookupOrgByHostname(document.location.hostname);
        }

        setOrganization(org);
        const config = await fetchConfigRaw(undefined, org.path);
        setUiConfig(config);
      } catch (err: unknown) {
        console.error(`Could not fetch configuration:`, err);
        if (err instanceof Error) setFetchError(err);
        else setFetchError(Error("An unkown error occurred."));
      }
    };
    fetch();
  }, [orgPath]);

  const store = React.useMemo(() => {
    return getConfiguredStore(uiConfig);
  }, [uiConfig]);

  const persistor = React.useMemo(() => {
    return persistStore(store);
  }, [store]);

  if (fetchError)
    return (
      <GenericError
        message={`Could not load organization '${
          organization?.path || orgPath
        }': ${fetchError.message}`}
      ></GenericError>
    );

  if (store === undefined || persistor === undefined || uiConfig === undefined)
    return (
      <ChakraProvider>
        {" "}
        <Flex height="100vh" justifyContent="center" alignItems="center">
          <Loading />
        </Flex>
      </ChakraProvider>
    );

  if (!organization)
    return <GenericError message="Organization is not defined."></GenericError>;

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <App initialConfig={uiConfig} organization={organization} />
      </PersistGate>
    </Provider>
  );
};

export const Router = () => {
  const conditionalRoutes = pathRoutingEnabled() ? (
    <Route path={`/:orgPath`} element={null}>
      <Route path={`:placementId`} element={null} />
    </Route>
  ) : (
    <Route path={`/:placementId`} element={null} />
  );
  return (
    <Routes>
      <Route path={"/"} element={<Root />}>
        {conditionalRoutes}
      </Route>
    </Routes>
  );
};
